import {getUserData} from './API';
import {Storage} from 'aws-amplify';
import {uuidToHEX, logSentryException} from './util';
import device from './device/device.js';
import stringify from 'json-stringify-safe';
import { interventionType } from '../constants/interventionSelectionConstants.js';
import { FEATURE_FLAGS } from '../constants/featureFlags.js';
import crypto from 'crypto';
import consoleLogger from './consoleLogger';

let utteranceLogs = [];

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

// Dedicated folder under LOGGINGBUCKET for the small, queryable set of story-milestone
// logs (TT-1344). Kept separate from the noisy per-activity session logs so tutor abandon
// rate can be scanned live without walking the full session-log tree.
const STORY_METRICS_FOLDER = 'story_metrics';

/**
 * Helper function to retry a promise-returning operation with exponential delay.
 * 
 * @param {Function} operation - Function that returns a Promise
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} delayMs - Delay in milliseconds between retries
 * @param {number} attempt - Current attempt number (used internally for recursion)
 * @param {string} operationName - Optional name for the operation (for Sentry logging)
 * @param {Object} extraData - Optional extra data to log to Sentry on final failure
 * @returns {Promise} - Resolves with operation result or rejects after all retries fail
 */
async function retryWithDelay(operation, maxRetries = MAX_RETRIES, delayMs = RETRY_DELAY_MS, attempt = 1, operationName = null, extraData = {}) {
  try {
    return await operation();
  } catch (err) {
    if (attempt >= maxRetries) {
      // Log to Sentry after all retries have failed
      if (operationName && FEATURE_FLAGS.ENABLE_COMMON_SENTRY_ERROR_LOGGING) {
        logSentryException(new Error(operationName), undefined, {
          errMessage: err.message,
          errName: err.name,
          errCode: err.code,
          ...extraData
        });
      }
      // Reject with the error
      throw err;
    }
    
    console.warn(`Operation failed (attempt ${attempt}/${maxRetries}), retrying in ${delayMs}ms...`, err.message);
    
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delayMs));
    
    // Retry the operation
    return retryWithDelay(operation, maxRetries, delayMs, attempt + 1, operationName, extraData);
  }
}

// Resolve the S3 key a session-log event is written under: an explicit
// forceKey, else the activityId, else the hex-ified userId (matching where
// pre-activity logs already land). Returns null when none is available yet.
function resolveSessionLogKey(sessionInfo, forceKey){
  if (forceKey) return forceKey;
  if (!sessionInfo) return null;
  if (sessionInfo.activityId) return sessionInfo.activityId;
  if (sessionInfo.userId) return uuidToHEX(sessionInfo.userId);
  return null;
}

// TT-1378: buffer for keyless session-log events awaiting a resolvable key.
// WebGLContext is the app root, so its render-lifecycle events fire at boot
// before login populates a userId/activityId; without buffering, exactly the
// render telemetry CR-10713 needs is dropped on the floor. Bounded so a
// never-authenticated tab (e.g. a stuck login) can't grow it without limit.
const MAX_BUFFERED_SESSION_LOGS = 200;
let bufferedSessionLogs = [];

// Perform the actual S3 put for one event. eventTs is the capture time, used in
// the filename so a buffered event keeps its real ordering after a late flush.
function putSessionLog(inType, inData, activity, eventTs){
  let stringifiedData;
  try {
    stringifiedData = stringify(inData);
  } catch (e) {
    // Telemetry must never throw into a caller (see logSessionInfoOriginal):
    // a payload that can't be serialized degrades to '{}' rather than throwing.
    stringifiedData = '{}';
  }

  // Validate that we have data to upload - if not, send empty object
  if (!stringifiedData || stringifiedData === 'null' || stringifiedData === 'undefined') {
    stringifiedData = '{}';
  }

  const options = {
    bucket: process.env.LOGGINGBUCKET + "/" + activity,
    contentType: 'application/json',
    level: "public",
    region: process.env.S3_BUCKET_REGION || 'us-east-2',
    customPrefix: {
      public: "",
    },
  };

  const fileName = inType + "_" + eventTs + ".json";

  return retryWithDelay(
    () => Storage.put(fileName, stringifiedData, options),
    MAX_RETRIES,
    RETRY_DELAY_MS,
    1,
    'logSessionInfo put failed',
    {
      eventType: inType,
      fileName: fileName,
      bucket: options.bucket,
      activityId: activity,
      hasData: !!stringifiedData
    }
  )
    .then(() => {
      return Promise.resolve(true);
    })
    .catch(err => {
      console.log("LOGGING PUT FAIL after retries:", err);
      return Promise.resolve(false);
    });
}

/**
 * Flush buffered keyless session-log events once a key is resolvable. No-op when
 * logging is disabled, the buffer is empty, or there is still no key. Each event
 * keeps its original capture timestamp, so the S3 filename order reflects when it
 * happened, not when it was flushed.
 *
 * Boot events flush under the hex-userId key (activityId isn't set until a story
 * starts), while later events (e.g. webgl_first_frame) land under the activityId
 * key. To keep the two joinable across prefixes, each flushed event is stamped
 * with `correlationUserId` (TT-1378). PUTs are issued sequentially rather than
 * as a concurrent burst: the flush fires at login, the most memory-contended
 * moment on the 4 GB devices this instruments, so a burst would perturb the very
 * measurement. Async + try/catch guarantees it never throws into a caller.
 */
export async function flushBufferedSessionLogs(){
  try {
    if (!FEATURE_FLAGS.SESSION_LOGS || bufferedSessionLogs.length === 0) {
      return true;
    }
    const sessionInfo = getUserData();
    const activity = resolveSessionLogKey(sessionInfo, null);
    if (!activity) return true;

    const correlationUserId = sessionInfo && sessionInfo.userId ? sessionInfo.userId : null;
    const pending = bufferedSessionLogs;
    bufferedSessionLogs = [];
    for (const evt of pending) {
      const data = (evt.inData && typeof evt.inData === 'object' && !Array.isArray(evt.inData))
        ? { correlationUserId, ...evt.inData }
        : evt.inData;
      await putSessionLog(evt.inType, data, activity, evt.ts);
    }
    return true;
  } catch (e) {
    try { console.log('flushBufferedSessionLogs error (non-fatal):', e); } catch (_) {}
    return false;
  }
}

// Original implementation of logSessionInfo
//
// Wrapped in try/catch so it can NEVER throw synchronously into a caller. Call
// sites include hot, unguarded paths — the WebGLContext render loop (a throw
// there would skip the requestAnimationFrame reschedule and freeze the avatar),
// init(), and the app-root componentDidMount — so a telemetry fault must degrade
// to a no-op, not manufacture the very white screen this instruments (TT-1378).
function logSessionInfoOriginal(inType,inData,forceKey=null,bufferIfNoKey=false){
  try {
    if (!FEATURE_FLAGS.SESSION_LOGS) {
      return Promise.resolve(true);
    }

    const activity = resolveSessionLogKey(getUserData(), forceKey);

    if (activity) {
      // A key exists now: drain anything buffered first so boot-time events land
      // in their original order ahead of the current one. Fire-and-forget; the
      // flush is async and self-contained (never throws).
      if (bufferedSessionLogs.length) {
        flushBufferedSessionLogs();
      }
      return putSessionLog(inType, inData, activity, Date.now());
    }

    // No resolvable key yet. Buffer only when the caller opts in (render-lifecycle
    // events); everything else keeps the prior drop-on-the-floor behavior.
    if (bufferIfNoKey && bufferedSessionLogs.length < MAX_BUFFERED_SESSION_LOGS) {
      bufferedSessionLogs.push({ inType, inData, ts: Date.now() });
    }

    // If no activity found, return resolved Promise instead of undefined
    return Promise.resolve(true);
  } catch (e) {
    try { console.log('logSessionInfo error (non-fatal):', e); } catch (_) {}
    return Promise.resolve(false);
  }
}

// Export wrapped version that captures logs for debug menu
export const logSessionInfo = consoleLogger.interceptLogSessionInfo(logSessionInfoOriginal);

/**
 * logSessionInfo variant that buffers the event until a userId/activityId key is
 * available (see flushBufferedSessionLogs). Use for events that can fire before
 * login resolves a key — notably the WebGLContext render lifecycle at app boot
 * (TT-1378). Routes through the same intercepted logSessionInfo so debug-menu
 * capture still sees it.
 */
export function logSessionInfoBuffered(inType, inData, forceKey=null){
  return logSessionInfo(inType, inData, forceKey, true);
}

export function logSystemData(){
  if (!FEATURE_FLAGS.SYSTEM_LOGS) {
    return;
  }

  logSessionInfo("browser",{browser: getUserData().browser,platform: navigator.platform,touchPoints: navigator.maxTouchPoints});
  logSessionInfo("network",getUserData().connection);
  logSessionInfo("device",device);

  //Remove the sessionData, which has PII and can get quite large over time
  let sessionInfo = JSON.parse(JSON.stringify(getUserData()));
  delete sessionInfo.sessionData;

  logSessionInfo("session",sessionInfo);
}

export function getFormattedUTCDate() {
  const date = new Date(); // This gets the current date and time
  const year = date.getUTCFullYear(); // Gets the year (4 digits)
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Gets the month, adds 1, and pads if necessary
  const day = String(date.getUTCDate()).padStart(2, '0'); // Gets the day of the month and pads if necessary

  return `${year}-${month}-${day}`; // Formats and returns the date as YYYY-MM-DD
}

export function logInterventionData(features, selection, forceIntervention = null) {
  if (!FEATURE_FLAGS.INTERVENTION_LOGS) {
    return;
  }

  const scope = features.interactions.scope;
  const { phraseIndex, words } = features.story.textMetadataForCurrentPhrase;
  const {
    index: wordIndex,
    word,
    type,
  }  = selection;

  if (scope === 'phrase' && (word === null || word === undefined) && type === interventionType.none) {
    // Do not save intervention data if no valid errors at end of phrase
    return Promise.resolve();
  }

  // Do not save intervention data if all interventions in phraseInterventionWeights are found in the history for the given word
  if (
    scope === 'phrase' &&
    wordIndex !== undefined &&
    wordIndex !== null &&
    selection.history &&
    selection.history.length > 0 &&
    selection.phraseInterventionWeights &&
    selection.phraseInterventionWeights.length > 0
  ) {
    const { phraseInterventionWeights, history } = selection;

    const interventionOptionsForWord = Object.keys(phraseInterventionWeights[wordIndex]);
    const historyInterventionTypesForWord = [];
    history.forEach((phraseInterventions, internalPhraseIndex) => {
      phraseInterventions.forEach((intervention) => {
        // Ignore interventions for other words
        if (intervention.word !== word) return;

        // Ignore the intervention that was just selected
        if (internalPhraseIndex === phraseIndex && intervention.type === type) return;

        historyInterventionTypesForWord.push(intervention.type);
      });
    });
    
    const intersection = interventionOptionsForWord.filter(type => historyInterventionTypesForWord.includes(type));
    
    if (intersection.length === interventionOptionsForWord.length) {
      return Promise.resolve();
    }
  }

  const options = {
    bucket: process.env.INTERVENTIONBUCKET + "/" + getFormattedUTCDate() + "/" + features.activityId,
    contentType: 'application/json',
    level: "public",
    region: process.env.S3_BUCKET_REGION || 'us-east-2',
    customPrefix: {
      public: "",
    },
  };

  const locale = features.locale;
  const {
    isEarlyReader: isEnglishEarlyReader,
    isSpanishEarlyReader
  } = features.student;
  const isEarlyReader =
      (locale.toLowerCase() === "en-us" && isEnglishEarlyReader) ||
      (locale.toLowerCase() === "es-mx" && isSpanishEarlyReader);

  let isOverride = false;
  if (words && words.length > wordIndex) {
    const wordMetadata = words[wordIndex];
    if (wordMetadata && wordMetadata.IS_OVERRIDE) {
      isOverride = true;
    }
  }

  const eventName = `phrase_${phraseIndex}_scope_${scope}_locale_${locale}_erss_${isEarlyReader}_override_${isOverride}_word_${word}_selected_${type}_force_${forceIntervention}`;

  const data = {
    wordIndex,
    word,
    type,
    history: selection.history,
    student: features.student,
    metadata: selection.metadata,
    interventionSelectionVersions: selection.versions,
    passSelected: selection.passSelected,
    log: selection.log,
    interactions: features.interactions,
    storyId: features.story.storyId,
    fallback: selection.fallback,
    closeError: selection.closeError,
  }

  let stringifiedData = stringify(data);
  
  // Validate that we have data to upload - if not, send empty object
  if (!stringifiedData || stringifiedData === 'null' || stringifiedData === 'undefined') {
    stringifiedData = '{}';
  }

  return retryWithDelay(() => Storage.put(eventName, stringifiedData, options))
    .then(() => {
      return Promise.resolve(true);
    })
    .catch(err => {
      console.log("INTERVENTION LOGGING PUT FAIL after retries:", err);
      return Promise.resolve(false);
    });
}

/**
 * Logs intervention-related events to the intervention bucket with a simple title and data format.
 * Similar to logSessionInfo but saves to the intervention bucket.
 * 
 * @param {string} eventTitle - The title/name of the event to log
 * @param {Object} inData - The data to log
 * @param {string} activityId - The activity ID for bucketing (required)
 * @returns {Promise<boolean>} - Promise that resolves to true on success, false on failure
 */
export function logInterventionEvent(eventTitle, inData, activityId) {
  if (!FEATURE_FLAGS.INTERVENTION_LOGS) {
    return Promise.resolve(true);
  }

  if (!activityId) {
    console.warn('logInterventionEvent: No activityId provided');
    return Promise.resolve(false);
  }

  const options = {
    bucket: process.env.INTERVENTIONBUCKET + '/' + getFormattedUTCDate() + '/' + activityId,
    contentType: 'application/json',
    level: 'public',
    region: process.env.S3_BUCKET_REGION || 'us-east-2',
    customPrefix: {
      public: '',
    },
  };

  let stringifiedData = stringify(inData);
  
  // Validate that we have data to upload - if not, send empty object
  if (!stringifiedData || stringifiedData === 'null' || stringifiedData === 'undefined') {
    stringifiedData = '{}';
  }

  return retryWithDelay(
    () => Storage.put(eventTitle, stringifiedData, options),
    MAX_RETRIES,
    RETRY_DELAY_MS,
    1,
    'logInterventionEvent put failed',
    {
      eventTitle: eventTitle,
      activityId: activityId,
      hasData: !!stringifiedData
    }
  )
    .then(() => {
      return Promise.resolve(true);
    })
    .catch(err => {
      console.log('INTERVENTION EVENT LOGGING PUT FAIL after retries:', err);
      return Promise.resolve(false);
    });
}

/**
 * Writes a single story-milestone JSON file for a tutor activity to the story-metrics
 * folder under LOGGINGBUCKET, mirroring the intervention-log date/activityId layout
 * (LOGGINGBUCKET/story_metrics/{UTC date}/{activityId}/{event}). Presence or absence of
 * these keys is the signal used to track tutor abandon rate in near-real time (TT-1344).
 *
 * Tutor-only gating is enforced by the caller (ReaderManager only calls this when not in
 * assessmentMode); this function does not inspect activity type. There is intentionally no
 * feature flag — these writes are always on. Writes are best-effort: a failure resolves to
 * false rather than throwing, so a metric-log failure never blocks the student flow.
 *
 * The date folder is pinned by the caller and passed in as dateFolder so every milestone for
 * one activity lands in the same day folder. Resolving the date per-write instead would split
 * a session that crosses midnight UTC across two day folders, making the "list a day's folder,
 * find who has no story_ended" abandon scan report false positives (TT-1344). When dateFolder
 * is omitted it falls back to the current UTC date.
 *
 * @param {string} eventFileName - The S3 key for this milestone, e.g. 'activity_created.json'.
 * @param {Object} inData - The JSON payload to store (kept minimal; presence is the signal).
 * @param {string} activityId - The activity ID for date/activityId bucketing (required).
 * @param {string} [dateFolder] - Pinned UTC YYYY-MM-DD day folder; defaults to today (UTC).
 * @returns {Promise<boolean>} - Resolves true on success, false on failure or missing activityId.
 */
export function logStoryMetric(eventFileName, inData, activityId, dateFolder = getFormattedUTCDate()) {
  if (!activityId) {
    console.warn('logStoryMetric: No activityId provided');
    return Promise.resolve(false);
  }

  const options = {
    bucket: process.env.LOGGINGBUCKET + '/' + STORY_METRICS_FOLDER + '/' + dateFolder + '/' + activityId,
    contentType: 'application/json',
    level: 'public',
    region: process.env.S3_BUCKET_REGION || 'us-east-2',
    customPrefix: {
      public: '',
    },
  };

  let stringifiedData = stringify(inData);

  // Validate that we have data to upload - if not, send empty object
  if (!stringifiedData || stringifiedData === 'null' || stringifiedData === 'undefined') {
    stringifiedData = '{}';
  }

  return retryWithDelay(
    () => Storage.put(eventFileName, stringifiedData, options),
    MAX_RETRIES,
    RETRY_DELAY_MS,
    1,
    'logStoryMetric put failed',
    {
      eventFileName: eventFileName,
      activityId: activityId,
      hasData: !!stringifiedData
    }
  )
    .then(() => {
      return Promise.resolve(true);
    })
    .catch(err => {
      console.log('STORY METRIC LOGGING PUT FAIL after retries:', err);
      return Promise.resolve(false);
    });
}

export function logCorruptedSpeechString(str) {
  if (!FEATURE_FLAGS.SPEECH_LOGS) {
    return;
  }

  // Use empty string as fallback for invalid input
  const validStr = (str && typeof str === 'string') ? str : '';
  if (!str || typeof str !== 'string') {
    console.warn('logCorruptedSpeechString: No valid string provided.');
    return;
  }

  const options = {
    bucket: process.env.LOGGINGBUCKET + "/corrupted_speech_strings",
    contentType: 'application/json',
    level: "public",
    region: process.env.S3_BUCKET_REGION || 'us-east-2',
    customPrefix: {
      public: "",
    },
  };

  const data = {
    string: validStr
  }

  let stringifiedData = stringify(data);
  
  // Validate that we have data to upload - if not, send empty object
  if (!stringifiedData || stringifiedData === 'null' || stringifiedData === 'undefined') {
    console.warn('logCorruptedSpeechString: Failed to stringify data - sending empty object');
    return Promise.resolve(false);
  }

  // Use hash of valid string, or a default hash for empty string
  const fileHash = crypto.createHash('md5').update(validStr).digest('hex');

  return Storage.put(fileHash + ".json", stringifiedData, options)
  .then(()=>{
    return Promise.resolve(true);
  })
  .catch(err => {
    console.log("LOGGING PUT FAIL:",err);
    return Promise.resolve(false);
  });
}

export function storeUtteranceLog(params) {
  if (!FEATURE_FLAGS.UTTERANCE_LOGS) {
    return;
  }

  utteranceLogs.push({
    ...params,
    timestamp: Date.now()
  });
}

export function getUtteranceLogs() {
  if (!FEATURE_FLAGS.UTTERANCE_LOGS) {
    return [];
  }

  return utteranceLogs;
}

export function clearUtteranceLogs() {
  utteranceLogs = [];
}